import { ColDef, ProcessCellForExportParams, ValueFormatterParams } from 'ag-grid-enterprise';
import { Decimal } from 'decimal.js';
import { CustomDataType } from '@/modules/common/components/ag-table/columns/common';
import { formatAnyDate, ifTimestampToDate, parseDate } from '@/utils/helpers/dates';
import { PbDateSchema, TimestampSchema, isMessage } from '@/connect';

export function processCsvCellCallback(params: ProcessCellForExportParams): string {
  return processCellCallback(params, false);
}

export function processExcelCellCallback(params: ProcessCellForExportParams): string {
  return processCellCallback(params, true);
}

function processCellCallback(params: ProcessCellForExportParams, excel: boolean): string {
  const colDef = params.column.getColDef();

  // force using the valueFormatter if useValueFormatterForExport is true,
  // instead of the bad default, which is to ignore this for numeric values (which includes our enums)
  if (
    colDef.valueFormatter &&
    typeof colDef.valueFormatter !== 'string' &&
    colDef.useValueFormatterForExport
  ) {
    const valueFormatterParams: ValueFormatterParams = {
      ...params,
      data: params.node?.data,
      node: params.node!,
      colDef,
    };

    const res = colDef.valueFormatter(valueFormatterParams);
    if (excel && colDef.cellDataType === CustomDataType.CUSIP) {
      return prepareCusipForExcel(res);
    }
    return res;
  }

  let value = params.value;

  switch (colDef.cellDataType) {
    case 'date':
      return formatAnyDate(value, 'MM/dd/yyyy');
    case 'dateString':
      if (typeof value === 'string') {
        try {
          value = parseDate(value, 'yyyy-MM-dd');
        } catch {
          // ignore
        }
      }
      return formatAnyDate(value, 'MM/dd/yyyy');
    case CustomDataType.Timestamp:
      return formatAnyDate(value, 'MM/dd/yyyy HH:mm');
    case CustomDataType.TimestampWithSeconds:
      return formatAnyDate(value, 'MM/dd/yyyy HH:mm:ss');
    case CustomDataType.TimestampAsDate:
    case CustomDataType.TimestampAsDateWithYear:
      return formatAnyDate(value, 'MM/dd/yyyy');
    case CustomDataType.CUSIP:
      if (excel) {
        return prepareCusipForExcel(value);
      }
      break;
  }

  if (value === null || value === undefined) {
    return '–';
  }

  if (isMessage(value, PbDateSchema)) {
    return formatAnyDate(value, 'MM/dd/yyyy');
  } else if (isMessage(value, TimestampSchema)) {
    return formatAnyDate(ifTimestampToDate(value), 'MM/dd/yyyy HH:mm:ss');
  } else if (value instanceof Date) {
    return formatAnyDate(value, 'MM/dd/yyyy HH:mm:ss');
  } else if (value instanceof Decimal) {
    const dp = value.decimalPlaces();
    return value.toFixed(Math.min(dp, 4));
  } else if (typeof value === 'bigint') {
    return `${value}`;
  }

  return `${value}`;
}

export function prepareCusipForExcel(value: unknown): string {
  // we want to make sure CUSIPs don't show up as numbers in excel,
  // to avoid that we prefix it with a '
  if (typeof value === 'string' && cusipNeedsEscapingInExcel(value)) {
    return `'${value}`;
  }

  return value as string;
}

const cusipNeedsEscapingRegex = /^[e0-9]{9}$/i;

export function cusipNeedsEscapingInExcel(value: string): boolean {
  return cusipNeedsEscapingRegex.test(value);
}

export function columnKeysExcl(columns: ColDef[], ...excludeColIds: string[]): string[] {
  return columns
    .map((colDef) => {
      if (colDef.headerCheckboxSelection) {
        return '';
      }
      if (!colDef.colId) {
        return '';
      }
      if (excludeColIds && excludeColIds.indexOf(colDef.colId) !== -1) {
        return '';
      }

      return colDef.colId;
    })
    .filter((colKey) => colKey !== '');
}
